"use client";
import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useAppAuth } from "@/hooks/useAppAuth";
import dynamic from "next/dynamic";
const LoginModal = dynamic(() => import("./LoginModal"), { ssr: false });
import Image from "next/image";
import {
  User,
  ChevronDown,
  MessageSquarePlus,
  ShieldCheck,
  LogOut,
  X,
  History,
  Globe,
  Wallet,
  Gift,
  Bot,
} from "lucide-react";

type Locale = "en" | "si" | "ta";

const LOCALE_OPTIONS: Array<{
  value: Locale;
  label: string;
  shortLabel: string;
}> = [
  { value: "en", label: "English", shortLabel: "EN" },
  { value: "si", label: "සිංහල", shortLabel: "සි" },
  { value: "ta", label: "தமிழ்", shortLabel: "TA" },
];

import { API_URL } from "@/lib/api-config";

const LOCALE_ROUTES: Record<Locale, string> = {
  en: "/",
  si: "/si",
  ta: "/ta",
};

const HOW_TO_TOPUP_ROUTES: Record<Locale, string> = {
  en: "/how-to-topup",
  si: "/si/how-to-topup",
  ta: "/ta/how-to-topup",
};
const SHOW_BCOINS = true;

interface NavbarProps {
  onLoginClick?: () => void;
  variant?: "dark" | "light";
  hideNav?: boolean;
  locale?: Locale;
}

const Navbar = ({
  onLoginClick,
  variant = "dark",
  hideNav = false,
  locale: propLocale = "en",
}: NavbarProps) => {
  const router = useRouter();
  const pathname = usePathname();
  const [queryLang, setQueryLang] = useState<Locale | null>(null);

  useEffect(() => {
    const handleUrlChange = () => {
      if (typeof window !== "undefined") {
        const params = new URLSearchParams(window.location.search);
        const lang = params.get("lang") as Locale;
        setQueryLang(lang || null);
      }
    };

    handleUrlChange();

    window.addEventListener("popstate", handleUrlChange);
    return () => {
      window.removeEventListener("popstate", handleUrlChange);
    };
  }, [pathname]);

  const pathLocale = pathname?.startsWith?.("/si")
    ? "si"
    : pathname?.startsWith?.("/ta")
      ? "ta"
      : null;
  const locale = queryLang || pathLocale || propLocale || "en";
  const isRoot = pathname === "/";
  const isBlog = pathname?.startsWith?.("/blog") || false;
  const {
    isAdmin,
    isSuperAdmin,
    isSubscriber,
    isAuthenticated,
    isAuthLoading,
    displayName,
    email,
    picture,
    rankFrame,
    logout,
    getToken,
    refreshSession,
  } = useAppAuth();

  const [scrolled, setScrolled] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMenuDrawerOpen, setIsMenuDrawerOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isLocaleMenuOpen, setIsLocaleMenuOpen] = useState(false);
  const [bcoinsBalance, setBcoinsBalance] = useState<number | null>(null);
  const [isBalanceLoading, setIsBalanceLoading] = useState(false);
  const [showEzcash, setShowEzcash] = useState(true);
  const localeMenuRef = useRef<HTMLDivElement>(null);

  const activeLocale =
    LOCALE_OPTIONS.find((option) => option.value === locale) ??
    LOCALE_OPTIONS[0];
  const isHowToTopupPage = pathname?.includes?.("/how-to-topup") || false;

  const handleLocaleChange = (nextLocale: Locale) => {
    setIsLocaleMenuOpen(false);
    if (nextLocale === locale) return;

    setQueryLang(nextLocale);

    if (pathname === "/" || pathname === "/si" || pathname === "/ta") {
      window.location.href = LOCALE_ROUTES[nextLocale];
    } else if (isHowToTopupPage) {
      window.location.href = HOW_TO_TOPUP_ROUTES[nextLocale];
    } else {
      const params = new URLSearchParams(window.location.search);
      params.set("lang", nextLocale);
      router.push(`${pathname}?${params.toString()}`);
    }
  };

  useEffect(() => {
    let active = true;
    const fetchPaymentConfig = async () => {
      try {
        const response = await fetch(`${API_URL}/public/payment-config`);
        if (response.ok) {
          const data = await response.json();
          const hasEzcash = data?.gateways?.some((g: any) => g.id === "EZCASH");
          if (active) {
            setShowEzcash(!!hasEzcash);
          }
        }
      } catch (err) {
        console.error("Failed to fetch payment config:", err);
      }
    };
    fetchPaymentConfig();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (isMenuDrawerOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuDrawerOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!localeMenuRef.current?.contains(event.target as Node)) {
        setIsLocaleMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    let isActive = true;

    const fetchBalance = async () => {
      if (isAuthLoading) {
        return;
      }

      if (!isAuthenticated) {
        setBcoinsBalance(null);
        setIsBalanceLoading(false);
        return;
      }

      setIsBalanceLoading(true);

      try {
        const token = await getToken();
        if (!token) {
          return;
        }

        const response = await fetch(`${API_URL}/bcoins/balance`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch BCoins balance");
        }

        const data = await response.json();
        const nextBalance = data?.balance ?? data?.data?.balance ?? 0;

        if (isActive) {
          setBcoinsBalance(
            typeof nextBalance === "number"
              ? nextBalance
              : Number(nextBalance) || 0,
          );
        }
      } catch {
        if (isActive) {
          setBcoinsBalance(null);
        }
      } finally {
        if (isActive) {
          setIsBalanceLoading(false);
        }
      }
    };

    void fetchBalance();

    return () => {
      isActive = false;
    };
  }, [getToken, isAuthLoading, isAuthenticated]);

  return (
    <>
      {/* Hamburger Drawer - rendered outside nav to avoid clipping */}
      {isMenuDrawerOpen && !hideNav && (
        <div key="menu-drawer" className="fixed inset-0 z-[99999] print:hidden">
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-md"
            style={{ animation: "fadeIn 0.2s ease-out forwards" }}
            onClick={() => setIsMenuDrawerOpen(false)}
          />
          <div
            className="fixed top-0 left-0 h-screen w-[320px] sm:w-[420px] max-w-[85vw] bg-white shadow-2xl border-r border-slate-200 flex flex-col z-[100000] antialiased"
            style={{ animation: "slideInLeft 0.3s ease-out forwards" }}
          >
            <div
              className={`flex items-center justify-between px-6 py-4 border-b border-slate-100 ${locale === "si" ? "font-sinhala-title" : "font-display"}`}
            >
              <span
                className={`font-bold text-[#5A98D8] ${locale === "en" ? "text-xl" : "text-2xl sm:text-3xl"}`}
              >
                {locale === "si" ? "මෙනුව" : locale === "ta" ? "மெனு" : "Menu"}
              </span>
              <button
                onClick={() => setIsMenuDrawerOpen(false)}
                aria-label={
                  locale === "si"
                    ? "මෙනුව වසන්න"
                    : locale === "ta"
                      ? "மெනුவை மூடு"
                      : "Close menu"
                }
                className="p-1 cursor-pointer hover:bg-slate-100 rounded-md transition-colors"
              >
                <X className="w-7 h-7 text-[#5A98D8]" />
              </button>
            </div>
            <nav
              className={`flex-1 flex flex-col gap-4 px-6 py-6 ${locale === "si" ? "font-sinhala-title" : "font-display"}`}
            >
              <Link
                href="/games"
                onClick={() => setIsMenuDrawerOpen(false)}
                className="font-bold text-[#548DC8] hover:text-[#5A98D8] transition-all duration-200 hover:translate-x-2 inline-block text-2xl sm:text-3xl"
              >
                {locale === "si"
                  ? "සියලුම ක්‍රීඩා"
                  : locale === "ta"
                    ? "அனைத்து கேம்களும்"
                    : "All Games"}
              </Link>
              <a
                href="/transactions"
                className={`font-bold text-[#548DC8] hover:text-[#5A98D8] transition-all duration-200 hover:translate-x-2 inline-block text-2xl sm:text-3xl`}
              >
                {locale === "si"
                  ? "සජීවී ගනුදෙනු"
                  : locale === "ta"
                    ? "நேரடி பரிவர்த்தனைகள்"
                    : "Live Transactions"}
              </a>
              <a
                href="/spin"
                className={`font-bold text-[#548DC8] hover:text-[#5A98D8] transition-all duration-200 hover:translate-x-2 inline-block text-2xl sm:text-3xl`}
              >
                {locale === "si"
                  ? "ලකී ස්පින්"
                  : locale === "ta"
                    ? "அதிர்ஷ்ட சுழற்சி"
                    : "Lucky Spin"}
              </a>
              <a
                href="/blog"
                className={`font-bold text-[#548DC8] hover:text-[#5A98D8] transition-all duration-200 hover:translate-x-2 inline-block text-2xl sm:text-3xl`}
              >
                {locale === "si"
                  ? "බ්ලොග්"
                  : locale === "ta"
                    ? "வலைப்பதிவுகள்"
                    : "Blogs"}
              </a>
              <a
                href="/about"
                className={`font-bold text-[#548DC8] hover:text-[#5A98D8] transition-all duration-200 hover:translate-x-2 inline-block text-2xl sm:text-3xl`}
              >
                {locale === "si"
                  ? "අපි ගැන"
                  : locale === "ta"
                    ? "எங்களைப் பற்றி"
                    : "About Us"}
              </a>
              <a
                href="/terms"
                className={`font-bold text-[#548DC8] hover:text-[#5A98D8] transition-all duration-200 hover:translate-x-2 inline-block text-2xl sm:text-3xl`}
              >
                {locale === "si"
                  ? "නියමයන් සහ කොන්දේසි"
                  : locale === "ta"
                    ? "விதிமுறைகள் மற்றும் நிபந்தனைகள்"
                    : "Terms & Conditions"}
              </a>
              <a
                href="/ezcash"
                className="font-bold text-amber-500 hover:text-amber-600 transition-all duration-200 hover:translate-x-2 inline-block text-2xl sm:text-3xl"
              >
                eZ Cash
              </a>
              <a
                href="/redeem-code"
                className="font-bold text-emerald-500 hover:text-emerald-600 transition-all duration-200 hover:translate-x-2 inline-block text-2xl sm:text-3xl"
              >
                Redeem Code
              </a>
            </nav>
          </div>
        </div>
      )}
      <nav
        className={`fixed top-0 left-0 right-0 z-[100] flex items-center justify-between px-3 sm:px-6 md:px-20 py-4 print:hidden ${
          scrolled
            ? "bg-white/70 backdrop-blur-md shadow-sm translate-y-0"
            : "bg-transparent translate-y-6 md:translate-y-8"
        }`}
        style={{
          transitionProperty: "background-color, transform, backdrop-filter",
          transitionDuration: "300ms",
        }}
      >
        <div className="flex items-center gap-1 sm:gap-2 print:hidden">
          {!hideNav && (
            <button
              onClick={() => setIsMenuDrawerOpen(true)}
              aria-label="Open menu"
            >
              <Image
                src="/assets/hamburger.svg"
                alt="Menu"
                width={20}
                height={20}
                className="w-5 h-5 transition-all mr-1.5"
                style={{
                  // On the root and blog listing when not scrolled, keep the white icon.
                  // For other pages or when scrolled (nav turns light), invert to dark.
                  filter: scrolled
                    ? "invert(1)"
                    : isRoot || isBlog
                      ? "none"
                      : "invert(1)",
                }}
              />
            </button>
          )}
        </div>
        <div className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 mt-3 md:mt-2 ">
          <Link
            href="/"
            onClick={(e) => {
              if (isRoot) {
                e.preventDefault();
                // Optionally scroll to absolute top if needed, but not to hero
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
            }}
          >
            <Image
              src="/assets/logo.svg"
              alt="Baasil Topup"
              width={260}
              height={66}
              priority
              fetchPriority="high"
              className={`object-contain p-1 cursor-pointer origin-left h-9 sm:h-12 md:h-16 w-auto ${scrolled ? "scale-[0.85] -translate-y-1 md:-translate-y-2" : "scale-100 translate-y-0"}`}
              style={{
                transitionProperty: "transform",
                transitionDuration: "300ms",
              }}
            />
          </Link>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 print:hidden">
          {isAuthenticated && SHOW_BCOINS && (
            <Link
              href="/ezcash"
              className="hidden md:flex items-center justify-center sm:justify-start gap-1 sm:gap-2 rounded-full border border-white/70 h-11 sm:px-3 sm:py-2 text-sm font-semibold hover:scale-[1.02] hover:shadow-sm shrink-0 select-none font-mono"
              style={{
                color: "#1a5b9b",
                backgroundColor: scrolled ? "rgba(255,255,255,0.95)" : "white",
                boxShadow: scrolled ? "0 2px 8px rgba(0,0,0,0.05)" : "none",
                transitionProperty: "background-color, transform",
                transitionDuration: "300ms",
              }}
            >
              <Image
                src="/assets/ez-cash-logo.png"
                alt="eZ Cash"
                width={18}
                height={18}
                className="object-contain shrink-0"
              />
              <span className="text-xs font-bold font-mono">
                {isBalanceLoading
                  ? "..."
                  : bcoinsBalance === null
                    ? "0"
                    : bcoinsBalance.toLocaleString()}
              </span>
            </Link>
          )}

          <div className="relative" ref={localeMenuRef}>
            <button
              type="button"
              onClick={() => setIsLocaleMenuOpen((open) => !open)}
              aria-label={
                locale === "si"
                  ? "භාෂාව තෝරන්න"
                  : locale === "ta"
                    ? "மொழியைத் தேர்ந்தெடுக்கவும்"
                    : "Select language"
              }
              aria-haspopup="menu"
              aria-expanded={isLocaleMenuOpen}
              className="flex items-center justify-center sm:justify-start gap-1 sm:gap-2 rounded-full border border-white/70 h-11 w-11 sm:h-11 sm:w-auto sm:px-3 sm:py-2 text-sm font-semibold hover:scale-[1.02] hover:shadow-sm"
              style={{
                color: "#1a5b9b",
                backgroundColor: scrolled ? "rgba(255,255,255,0.95)" : "white",
                boxShadow: scrolled ? "0 2px 8px rgba(0,0,0,0.05)" : "none",
                transitionProperty: "background-color, transform",
                transitionDuration: "300ms",
              }}
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#548DC8]/10 text-[#1a5b9b] text-xs font-bold sm:-ml-1 shrink-0">
                <Globe className="hidden sm:block h-4 w-4" />
                <span className="sm:hidden uppercase leading-none">
                  {activeLocale.shortLabel}
                </span>
              </span>
              <span className="hidden sm:inline text-xs uppercase tracking-[0.22em]">
                {activeLocale.shortLabel}
              </span>
              <ChevronDown
                className={`h-3.5 w-3.5 transition-transform hidden sm:inline-block ${isLocaleMenuOpen ? "rotate-180" : ""}`}
              />
            </button>

            {isLocaleMenuOpen && (
              <>
                <div
                  className="fixed inset-0 z-[110]"
                  onClick={() => setIsLocaleMenuOpen(false)}
                />
                <div
                  role="menu"
                  className="absolute right-0 mt-3 w-44 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl z-[120]"
                  style={{ animation: "fadeSlideIn 0.2s ease-out" }}
                >
                  {LOCALE_OPTIONS.map((option) => {
                    const isSelected = option.value === locale;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        role="menuitem"
                        onClick={() => handleLocaleChange(option.value)}
                        className={`flex w-full items-center justify-between px-4 py-3 text-left transition-colors hover:bg-slate-50 ${isSelected ? "bg-[#548DC8]/10" : ""}`}
                      >
                        <span className="text-sm font-medium text-slate-900">
                          {option.label}
                        </span>
                        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#1a5b9b]">
                          {option.shortLabel}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {isAuthenticated ? (
            <div className="relative">
              <button
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                className="flex items-center justify-center rounded-full h-11 w-11 sm:h-11 sm:w-auto sm:px-3 sm:py-2 group"
                style={{
                  backgroundColor: scrolled ? "rgba(255,255,255,0.9)" : "white",
                  boxShadow: scrolled
                    ? "0 2px 8px rgba(0,0,0,0.05)"
                    : "0 1px 4px rgba(0,0,0,0.06)",
                  transitionProperty: "background-color, transform",
                  transitionDuration: "300ms",
                }}
              >
                <div className="relative w-8 h-8 md:w-10 md:h-10 group-hover:scale-110 transition-transform sm:-ml-1.5 shrink-0">
                  <Image
                    src={(() => {
                      if (!picture) return "/assets/avatar-testimonial.png";
                      if (picture.startsWith("http")) return picture;
                      const apiBase = (
                        process.env.NEXT_PUBLIC_API_URL ||
                        "http://localhost:3000/api"
                      ).replace("/api", "");
                      return `${apiBase}${picture.startsWith("/") ? picture : `/${picture}`}`;
                    })()}
                    alt="Profile"
                    width={40}
                    height={40}
                    sizes="40px"
                    className="absolute inset-0 w-full h-full rounded-full object-cover  -mt-0.5"
                    style={{ padding: "20%" }}
                    unoptimized
                    onError={(e) => {
                      (e.target as HTMLImageElement).srcset = "";
                      (e.target as HTMLImageElement).src =
                        "/assets/avatar-testimonial.png";
                    }}
                  />
                  {rankFrame && (
                    <Image
                      src={rankFrame}
                      alt="Rank"
                      width={64}
                      height={64}
                      sizes="64px"
                      className="absolute inset-0 w-full h-full object-contain pointer-events-none z-10"
                      style={{ transform: "scale(1.6)" }}
                    />
                  )}
                  <div
                    className="absolute inset-0 pointer-events-none z-20 rounded-full overflow-hidden"
                    style={{ transform: "scale(1.6)" }}
                  >
                    <div
                      className="absolute inset-0"
                      style={{
                        background:
                          "linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.5) 45%, rgba(255,255,255,0.8) 50%, rgba(255,255,255,0.5) 55%, transparent 60%)",
                        animation: "navFrameShine 3s ease-in-out infinite",
                      }}
                    />
                  </div>
                </div>
                <div className="hidden sm:flex flex-col items-start">
                  <span
                    className="text-sm font-semibold truncate max-w-[100px] md:max-w-[160px] leading-none font-mono"
                    style={{ color: "#1a5b9b" }}
                  >
                    {displayName}
                  </span>
                </div>
                <ChevronDown
                  className={`w-3.5 h-3.5 transition-transform duration-300 hidden sm:inline-block ${isUserMenuOpen ? "rotate-180" : ""}`}
                  style={{ color: "#1a5b9b" }}
                />
              </button>

              {isUserMenuOpen && (
                <>
                  <div
                    className="fixed inset-0 z-[110]"
                    onClick={() => setIsUserMenuOpen(false)}
                  />
                  <div
                    className="font-mono absolute right-0 mt-3 w-64 bg-white rounded-2xl border border-slate-200 shadow-2xl z-[120] overflow-hidden"
                    style={{ animation: "fadeSlideIn 0.2s ease-out" }}
                  >
                    <div className="p-4 bg-slate-50 border-b border-slate-100">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                          {locale === "si"
                            ? "ගිණුම"
                            : locale === "ta"
                              ? "கணக்கு"
                              : "ACCOUNT"}
                        </p>
                        {isSuperAdmin ? (
                          <span className="text-[9px] font-bold bg-indigo-600 text-white px-1.5 py-0.5 rounded leading-none">
                            SUPERADMIN
                          </span>
                        ) : isAdmin ? (
                          <span className="text-[9px] font-bold bg-amber-600 text-white px-1.5 py-0.5 rounded leading-none">
                            ADMIN
                          </span>
                        ) : null}
                      </div>
                      <p className="text-sm font-medium text-slate-900 truncate">
                        {email || displayName}
                      </p>
                      {SHOW_BCOINS && (
                        <div className="mt-2 flex items-center gap-1.5 text-xs text-[#1a5b9b] font-semibold bg-white border border-slate-100 rounded-lg px-2.5 py-1.5 shadow-sm">
                          <Image
                            src="/assets/ez-cash-logo.png"
                            alt="eZ Cash"
                            width={20}
                            height={20}
                            className="object-contain"
                          />
                          <span>
                            {isBalanceLoading
                              ? "..."
                              : bcoinsBalance === null
                                ? "0"
                                : bcoinsBalance.toLocaleString()}{" "}
                            eZ Cash
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="p-2 space-y-0.5">
                      <Link
                        href="/profile"
                        onClick={() => setIsUserMenuOpen(false)}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-50 transition-colors group"
                      >
                        <div className="w-8 h-8 rounded-lg bg-[#548DC8]/10 flex items-center justify-center group-hover:rotate-12 transition-transform">
                          <User className="w-4 h-4 text-[#548DC8]" />
                        </div>
                        <span className="text-[13px] font-medium text-slate-900">
                          {locale === "si"
                            ? "ප්‍රොෆයිල්"
                            : locale === "ta"
                              ? "சுயவிவரம்"
                              : "Profile"}
                        </span>
                      </Link>

                      <Link
                        href="/order-history"
                        onClick={() => setIsUserMenuOpen(false)}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-50 transition-colors group"
                      >
                        <div className="w-8 h-8 rounded-lg bg-[#548DC8]/10 flex items-center justify-center group-hover:rotate-12 transition-transform">
                          <History className="w-4 h-4 text-[#548DC8]" />
                        </div>
                        <span className="text-[13px] font-medium text-slate-900">
                          {locale === "si"
                            ? "ඇණවුම් ඉතිහාසය"
                            : locale === "ta"
                              ? "ஆர்டர் வரலாறு"
                              : "Order History"}
                        </span>
                      </Link>

                      <Link
                        href="/ezcash"
                        onClick={() => setIsUserMenuOpen(false)}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-amber-50 transition-colors group"
                      >
                        <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center group-hover:rotate-12 transition-transform">
                          <Wallet className="w-4 h-4 text-amber-600" />
                        </div>
                        <span className="text-[13px] font-medium text-slate-900">
                          eZ Cash
                        </span>
                      </Link>

                      <Link
                        href="/redeem-code"
                        onClick={() => setIsUserMenuOpen(false)}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-emerald-50 transition-colors group"
                      >
                        <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center group-hover:rotate-12 transition-transform">
                          <Gift className="w-4 h-4 text-emerald-600" />
                        </div>
                        <span className="text-[13px] font-medium text-slate-900">
                          Redeem Code
                        </span>
                      </Link>

                      {isAdmin && (
                        <Link
                          href="/admin"
                          onClick={() => setIsUserMenuOpen(false)}
                          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-50 transition-colors group"
                        >
                          <div className="w-8 h-8 rounded-lg bg-[#548DC8]/10 flex items-center justify-center group-hover:rotate-12 transition-transform">
                            <ShieldCheck className="w-4 h-4 text-[#548DC8]" />
                          </div>
                          <span className="text-[13px] font-medium text-slate-900">
                            {locale === "si"
                              ? "පරිපාලක පැනලය"
                              : locale === "ta"
                                ? "நிர்வாக குழு"
                                : "Admin Panel"}
                          </span>
                        </Link>
                      )}

                      {isSuperAdmin && (
                        <Link
                          href="/rgg-bot"
                          onClick={() => setIsUserMenuOpen(false)}
                          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-emerald-50 transition-colors group"
                        >
                          <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center group-hover:rotate-12 transition-transform">
                            <Bot className="w-4 h-4 text-emerald-600" />
                          </div>
                          <span className="text-[13px] font-bold text-emerald-700">
                            RGG Bot
                          </span>
                        </Link>
                      )}

                      <div className="!my-1.5 border-t border-slate-100" />

                      <button
                        onClick={() => {
                          setIsUserMenuOpen(false);
                          logout();
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-rose-50 transition-colors group text-rose-600"
                      >
                        <div className="w-8 h-8 rounded-lg bg-rose-500/10 flex items-center justify-center group-hover:-translate-x-1 transition-transform">
                          <LogOut className="w-4 h-4" />
                        </div>
                        <span className="text-[13px] font-medium">
                          {locale === "si"
                            ? "ඉවත් වන්න"
                            : locale === "ta"
                              ? "வெளியேறு"
                              : "Sign Out"}
                        </span>
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : (
            !hideNav && (
              <button
                onClick={onLoginClick || (() => setIsLoginModalOpen(true))}
                className="text-xs sm:text-sm uppercase tracking-wider px-2.5 py-2 sm:px-4 sm:py-2.5 hover:opacity-80 rounded-[2px]"
                style={{
                  color: "#1a5b9b",
                  backgroundColor: scrolled ? "rgba(255,255,255,0.9)" : "white",
                  boxShadow: scrolled ? "0 2px 8px rgba(0,0,0,0.05)" : "none",
                  transitionProperty: "background-color, opacity, transform",
                  transitionDuration: "300ms",
                }}
              >
                {locale === "si"
                  ? "පිවිසෙන්න"
                  : locale === "ta"
                    ? "உள்நுழைய"
                    : "Login"}
              </button>
            )
          )}
        </div>

        <style jsx>{`
          @keyframes fadeSlideIn {
            from {
              opacity: 0;
              transform: translateY(-8px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          @keyframes fadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }
          @keyframes slideInLeft {
            from {
              transform: translateX(-100%);
            }
            to {
              transform: translateX(0);
            }
          }
          .fade-in {
            animation: fadeIn 0.2s ease-out forwards;
          }
          .slide-in-left {
            animation: slideInLeft 0.3s ease-out forwards;
          }
          @keyframes navFrameShine {
            0% {
              transform: translateX(-150%) rotate(15deg);
              opacity: 0;
            }
            15% {
              opacity: 1;
            }
            50% {
              transform: translateX(150%) rotate(15deg);
              opacity: 0;
            }
            100% {
              transform: translateX(150%) rotate(15deg);
              opacity: 0;
            }
          }
        `}</style>
      </nav>
      {!onLoginClick && isLoginModalOpen && (
        <LoginModal
          key="navbar-login-modal"
          isOpen={isLoginModalOpen}
          onClose={() => setIsLoginModalOpen(false)}
          onLoginSuccess={() => {
            refreshSession();
          }}
        />
      )}
    </>
  );
};

export default Navbar;
